import { useState } from "react";
import SignatureCanvas from "./SignatureCanvas";
import PhotoPicker from "./PhotoPicker";

interface Photo {
  id: string;
  photo_url: string;
  sort_order: number;
}

export interface GuestEntry {
  guest_name: string;
  message: string;
  emoji: string;
  signature_data: string | null;
  photo_id: string | null;
  photo_url: string | null;
}

interface Props {
  photos: Photo[];
  accentColor: string;
  onSubmit: (entry: GuestEntry) => Promise<void>;
  disabled?: boolean;
}

const EMOJIS = ["❤️", "🎉", "🥂", "💐", "✨", "🌹", "🎊", "💫", "🙏", "😍", "🥰", "💕"];

export default function GuestBookForm({ photos, accentColor, onSubmit, disabled }: Props) {
  const [name,      setName]      = useState("");
  const [message,   setMessage]   = useState("");
  const [emoji,     setEmoji]     = useState("");
  const [signature, setSignature] = useState<string | null>(null);
  const [photoId,   setPhotoId]   = useState<string | null>(null);
  const [saving,    setSaving]    = useState(false);
  const [error,     setError]     = useState<string | null>(null);

  const selectedPhoto = photos.find((p) => p.id === photoId) ?? null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) { setError("Tu nombre es obligatorio."); return; }
    if (!message.trim() && !signature) { setError("Escribe un mensaje o deja tu firma."); return; }
    setError(null);
    setSaving(true);
    try {
      await onSubmit({
        guest_name:    name.trim(),
        message:       message.trim(),
        emoji,
        signature_data: signature,
        photo_id:       photoId,
        photo_url:      selectedPhoto?.photo_url ?? null,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error al guardar. Intenta de nuevo.");
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">

      {/* Name */}
      <div>
        <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">
          Tu nombre *
        </label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="¿Cómo te llamas?"
          maxLength={100}
          className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-700 focus:outline-none focus:ring-2 transition-all"
          style={{ focusRingColor: accentColor } as React.CSSProperties}
          disabled={disabled || saving}
        />
      </div>

      {/* Message */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
            Tu mensaje
          </label>
          <span className="text-xs text-slate-400">{message.length}/500</span>
        </div>
        <textarea
          rows={4}
          value={message}
          onChange={(e) => {
            if (e.target.value.length <= 500) setMessage(e.target.value);
          }}
          placeholder="Escribe algo especial para los festejados..."
          className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-700 focus:outline-none focus:ring-2 resize-none transition-all"
          disabled={disabled || saving}
        />
      </div>

      {/* Emoji */}
      <div>
        <label className="block text-xs font-semibold text-slate-600 mb-2 uppercase tracking-wide">
          Emoji (opcional)
        </label>
        <div className="flex flex-wrap gap-2">
          {EMOJIS.map((e) => (
            <button
              key={e}
              type="button"
              onClick={() => setEmoji(emoji === e ? "" : e)}
              disabled={disabled || saving}
              className={`w-10 h-10 flex items-center justify-center rounded-xl text-xl border-2 transition-all cursor-pointer ${
                emoji === e ? "border-current scale-110" : "border-transparent bg-slate-100 hover:bg-slate-200"
              }`}
              style={emoji === e ? { borderColor: accentColor + "60", backgroundColor: accentColor + "15" } : {}}
            >
              {e}
            </button>
          ))}
        </div>
      </div>

      {/* Signature */}
      <SignatureCanvas
        onSignature={setSignature}
        accentColor={accentColor}
      />

      {/* Photo picker */}
      {photos.length > 0 && (
        <PhotoPicker
          photos={photos}
          selectedId={photoId}
          onSelect={setPhotoId}
          accentColor={accentColor}
        />
      )}

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
          <i className="ri-error-warning-line text-red-400 shrink-0"></i>
          <p className="text-red-600 text-sm">{error}</p>
        </div>
      )}

      {/* Submit */}
      <button
        type="submit"
        disabled={disabled || saving}
        className="w-full flex items-center justify-center gap-2.5 py-4 rounded-2xl font-bold text-sm transition-all cursor-pointer whitespace-nowrap active:scale-95 disabled:opacity-50"
        style={{ backgroundColor: accentColor, color: "#ffffff" }}
      >
        {saving ? (
          <>
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            Guardando...
          </>
        ) : (
          <>
            <i className="ri-send-plane-fill text-base"></i>
            Dejar mi mensaje
          </>
        )}
      </button>
    </form>
  );
}